import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { AlertOctagon, Loader2 } from "lucide-react";

const BAR_COLORS = [
  "#C4553B", "#D99A2B", "#B8C784", "#8B5CF6", "#E67B6D", "#F0C572",
];

export default function LossReasonsWidget() {
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/loss-reasons?period=90d");
        if (alive) setData(data);
      } catch {
        if (alive) setData({ total_lost: 0, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="loss-reasons-widget">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-[#E67B6D]" />
            <span className="text-sm font-semibold text-slate-200">Pourquoi on perd</span>
          </div>
          <span className="text-xs text-slate-500">90 jours glissants</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : !data || data.total_lost === 0 ? (
          <div className="py-8 text-center text-xs text-slate-500" data-testid="loss-reasons-empty">
            Aucun lead marqué comme perdu sur cette période. 🎉
          </div>
        ) : (
          <div className="space-y-2" data-testid="loss-reasons-list">
            <div className="mb-2 text-xs text-slate-400">
              {data.total_lost} lead{data.total_lost > 1 ? "s" : ""} perdu{data.total_lost > 1 ? "s" : ""}
            </div>
            {data.items
              .filter((it) => it.count > 0)
              .map((it, idx) => (
                <div key={it.reason} className="space-y-1" data-testid={`loss-reason-row-${it.reason}`}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-slate-300">{it.label}</span>
                    <span className="tabular text-slate-400">
                      {it.count} · {it.pct}%
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
                    <div
                      style={{
                        width: `${it.pct}%`,
                        backgroundColor: BAR_COLORS[idx % BAR_COLORS.length],
                      }}
                      className="h-full transition-all"
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
